import { Box } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import clsx from "clsx";
import { FieldTitle, useInput } from "ra-core";
import { useCallback } from "react";
import { InputHelperText, sanitizeInputRestProps } from "react-admin";
import { hexToRgb, rgbToHex } from "../../util";
import type { TextInputProps } from "./input-wrappers";

export const ColorInput = (props: TextInputProps): JSX.Element => {
    const {
        className,
        defaultValue = [0, 0, 1],
        parse = (v: string): [number, number, number] => {
            return hexToRgb(v)
        },
        format = (v: [number, number, number]): string => {
            return !v ? "#000001" : rgbToHex(v)
        },
        label,
        fullWidth,
        helperText,
        onBlur,
        onChange,
        onFocus,
        disabled,
        resource,
        source,
        validate,
        sx,
        ...rest
    } = props;
    const {
        id,
        field,
        isRequired,
        fieldState: { error, invalid, isTouched },
        formState: { isSubmitted },
    } = useInput({
        defaultValue,
        format,
        parse,
        resource,
        source,
        onBlur,
        onChange,
        type: "checkbox",
        validate,
        ...rest,
    });

    const handleChange = useCallback(
        (event: Event) => {
            field.onChange(event);
            // Ensure field is considered as touched
            field.onBlur();
        },
        [field]
    );
    return (
        <FormGroup className={clsx("ra-input", `ra-input-${source}`, className)} sx={{ ...sx, paddingBottom: "1px", width: "100%", position: "relative" }}>
            <FormControlLabel
                labelPlacement="start"
                sx={{
                    height: 40,
                    margin: 0,
                    borderRadius: 1, 
                    border: `1px solid rgba(var(--mui-palette-common-onBackgroundChannel) / 0.23)`,
                    "& .MuiTypography-root": { width: "100%", paddingLeft: 2 }
                }}
                control={
                    <input
                        style={{ 
                            height: 38, 
                            width: "100%",
                            border: "0 solid transparent",
                            borderRadius: 16,
                            backgroundColor: "transparent",
                            opacity: field.value !== "#000001" ? 1: 0
                        }}
                        type="color"
                        id={id}
                        name={field.name}
                        color="primary"
                        onChange={handleChange}
                        onFocus={onFocus}
                        value={field.value}
                        {...sanitizeInputRestProps(rest)}
                        disabled={disabled}
                    />
                }
                label={
                    <Box sx={t => ({ color: t.palette.text.secondary })}>
                        <FieldTitle label={label} source={source} resource={resource} isRequired={isRequired} />
                    </Box>
                }
            />
            {helperText !== false && (
                <FormHelperText error={(isTouched || isSubmitted) && invalid}>
                    <InputHelperText error={error?.message} helperText={helperText} />
                </FormHelperText>
            )}
        </FormGroup>
    );
};
